import React, { useState } from 'react';
import { OnboardingData } from './OnboardingFlow';
import { ANIMAL_TYPES, PET_TYPES } from '../../../services/storeClubAPI';

interface OnboardingReviewProps {
  data: OnboardingData;
  updateData: (updates: Partial<OnboardingData>) => void;
  onBack: () => void;
  onSubmit: (password: string) => void;
  isSubmitting: boolean;
  error: string;
}

const OnboardingReview: React.FC<OnboardingReviewProps> = ({
  data,
  updateData,
  onBack,
  onSubmit,
  isSubmitting,
  error,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const getAnimalIcon = (type: string) => {
    return ANIMAL_TYPES.find((a) => a.value === type)?.icon || '🐾';
  };

  const getAnimalLabel = (type: string) => {
    return ANIMAL_TYPES.find((a) => a.value === type)?.label || type;
  };

  const handleSubmit = () => {
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordError('');
    // Pass password directly to onSubmit to avoid async state update race condition
    onSubmit(password);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Review & Join</h2>
      <p style={styles.subtext}>
        Take a moment to make sure everything looks right. You can always update this later.
      </p>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Your Information</h3>
        <div style={styles.card}>
          <div style={styles.row}>
            <span style={styles.label}>Name</span>
            <span style={styles.value}>{data.name}</span>
          </div>
          <div style={styles.row}>
            <span style={styles.label}>Email</span>
            <span style={styles.value}>{data.email}</span>
          </div>
          <div style={styles.row}>
            <span style={styles.label}>Phone</span>
            <span style={styles.value}>{data.phone}</span>
          </div>
          <div style={styles.row}>
            <span style={styles.label}>Address</span>
            <span style={styles.value}>
              {data.street_address}, {data.city}, {data.state} {data.zip_code}
            </span>
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Your Animals</h3>
        <div style={styles.animalGrid}>
          {data.animals.map((animal, index) => (
            <div key={index} style={styles.animalCard}>
              <span style={styles.animalIcon}>{getAnimalIcon(animal.animal_type)}</span>
              <div style={styles.animalInfo}>
                <span style={styles.animalName}>
                  {animal.name || getAnimalLabel(animal.animal_type)}
                </span>
                <span style={styles.animalDetails}>
                  {PET_TYPES.includes(animal.animal_type)
                    ? `${animal.weight_lbs} lbs`
                    : `${animal.count}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Preferences</h3>
        <div style={styles.card}>
          <div style={styles.prefRow}>
            <span style={styles.prefIcon}>
              {data.subscribe_to_reminders ? '✓' : '✗'}
            </span>
            <span style={{
              ...styles.prefText,
              color: data.subscribe_to_reminders ? '#333' : '#999',
            }}>
              Feed Reminders
            </span>
          </div>
          <div style={styles.prefRow}>
            <span style={styles.prefIcon}>
              {data.subscribe_to_weather_alerts ? '✓' : '✗'}
            </span>
            <span style={{
              ...styles.prefText,
              color: data.subscribe_to_weather_alerts ? '#333' : '#999',
            }}>
              Weather Alerts
            </span>
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Create Password</h3>
        <div style={styles.passwordForm}>
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>Password</label>
            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                placeholder="At least 8 characters"
              />
              <button
                type="button"
                style={styles.eyeButton}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? '👁' : '👁‍🗨'}
              </button>
            </div>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>Confirm Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              placeholder="Re-enter password"
            />
          </div>
          {passwordError && <span style={styles.error}>{passwordError}</span>}
        </div>
      </div>

      {error && (
        <div style={styles.errorBox}>
          {error}
        </div>
      )}

      <div style={styles.actions}>
        <button style={styles.backButton} onClick={onBack} disabled={isSubmitting}>
          Back
        </button>
        <button
          style={{
            ...styles.submitButton,
            opacity: isSubmitting ? 0.7 : 1,
          }}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating Account...' : 'Join Store Club'}
        </button>
      </div>

      <p style={styles.terms}>
        By joining, you agree to receive emails from Red Bridge Farm Feed Co.
        You can unsubscribe at any time.
      </p>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
    fontFamily: "'Open Sans', sans-serif",
  },
  subtext: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
    lineHeight: '1.5',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#508590',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: 0,
  },
  card: {
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: '1px solid #f0f0f0',
  },
  label: {
    fontSize: '0.875rem',
    color: '#666',
  },
  value: {
    fontSize: '0.875rem',
    color: '#333',
    fontWeight: 500,
    textAlign: 'right',
  },
  animalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '0.75rem',
  },
  animalCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    backgroundColor: '#fff',
    padding: '0.75rem',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  animalIcon: {
    fontSize: '1.5rem',
  },
  animalInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  animalName: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#333',
  },
  animalDetails: {
    fontSize: '0.75rem',
    color: '#666',
  },
  prefRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem 0',
  },
  prefIcon: {
    fontSize: '1rem',
    color: '#508590',
  },
  prefText: {
    fontSize: '0.875rem',
  },
  passwordForm: {
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    marginBottom: '1rem',
  },
  inputLabel: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#333',
  },
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    padding: '0.875rem 1rem',
    paddingRight: '3rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: "'Open Sans', sans-serif",
  },
  eyeButton: {
    position: 'absolute',
    right: '0.75rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.25rem',
    padding: '0.25rem',
  },
  error: {
    fontSize: '0.75rem',
    color: '#E73C3E',
  },
  errorBox: {
    backgroundColor: '#ffebee',
    color: '#E73C3E',
    padding: '0.875rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  backButton: {
    flex: 1,
    backgroundColor: 'transparent',
    color: '#666',
    border: '2px solid #e0e0e0',
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
  submitButton: {
    flex: 2,
    backgroundColor: '#E73C3E',
    color: '#fff',
    border: 'none',
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
  terms: {
    textAlign: 'center',
    fontSize: '0.75rem',
    color: '#999',
    margin: 0,
  },
};

export default OnboardingReview;
